'use client';

import { c as _c } from "react/compiler-runtime";
import { ChartTooltipPanel, FloatingTooltip } from '@/domains/pages/shared/chart-tooltip';
import { pieChartAccentFill } from '@/domains/pages/ai-proficiency/constants';
import { AIFI_DATA_NUMBER_CLASS } from '@/domains/pages/ai-proficiency/typography';
import { MODEL_ID_COLORS, MODEL_ORDER, MODEL_TIERS, MODEL_TIER_LABELS } from '@/domains/pages/model-savings/constants';
import type { ModelId, ModelMixSegment, SavingsCalculationRow } from '@/domains/pages/model-savings/types';
import { dominantMixSegment, formatAnnualSpend, mixPercent } from '@/domains/pages/model-savings/utils/model-savings.utils';
import { formatScore } from '@/utils/format.util';
import { cn } from '@/utils/shad';
import { Skeleton } from '@/comps/primitives/skeleton';
import { useCallback, useRef, useState } from 'react';
const DEFAULT_SIZE_PX = 30;

/** Ring proportions and slice separation copied from the savings opportunity donut. */
const RING_CUTOUT = 0.6;
const SLICE_GAP_DEG = 5;
const SLICE_BORDER_WIDTH_PX = 1;

/** Point on a circle, with 0 degrees at 12 o'clock. */
function polarPoint(centre: number, radius: number, angleDeg: number): [number, number] {
  const radians = (angleDeg - 90) * Math.PI / 180;
  return [centre + radius * Math.cos(radians), centre + radius * Math.sin(radians)];
}
function annularSectorPath(centre: number, outerRadius: number, innerRadius: number, startDeg: number, endDeg: number): string {
  const [outerStartX, outerStartY] = polarPoint(centre, outerRadius, startDeg);
  const [outerEndX, outerEndY] = polarPoint(centre, outerRadius, endDeg);
  const [innerEndX, innerEndY] = polarPoint(centre, innerRadius, endDeg);
  const [innerStartX, innerStartY] = polarPoint(centre, innerRadius, startDeg);
  const largeArc = endDeg - startDeg > 180 ? 1 : 0;
  return [`M ${outerStartX} ${outerStartY}`, `A ${outerRadius} ${outerRadius} 0 ${largeArc} 1 ${outerEndX} ${outerEndY}`, `L ${innerEndX} ${innerEndY}`, `A ${innerRadius} ${innerRadius} 0 ${largeArc} 0 ${innerStartX} ${innerStartY}`, 'Z'].join(' ');
}

/**
 * A ring in the savings opportunity donut's style: each tier is a translucent fill inside a
 * one-pixel border of its own colour. SVG rather than a chart library because a page of rows
 * holds twenty of these.
 */
function MixRing({
  mix,
  sizePx
}: {
  mix: ModelMixSegment[];
  sizePx: number;
}) {
  const centre = sizePx / 2;
  const outerRadius = centre - SLICE_BORDER_WIDTH_PX / 2;
  const innerRadius = outerRadius * RING_CUTOUT;
  const slices = MODEL_ORDER.map(modelId => ({
    modelId,
    percent: mixPercent(mix, modelId)
  })).filter(slice => slice.percent > 0);
  /** Percentages are rounded upstream, so normalise rather than leave a gap at the end. */
  const total = slices.reduce((sum, slice_0) => sum + slice_0.percent, 0);
  if (total <= 0) return <svg width={sizePx} height={sizePx} aria-hidden className="shrink-0" />;

  /** A lone tier spans the full turn, where an arc back to its start would draw nothing. */
  if (slices.length === 1) {
    const color = MODEL_ID_COLORS[slices[0]!.modelId];
    return <svg width={sizePx} height={sizePx} viewBox={`0 0 ${sizePx} ${sizePx}`} aria-hidden className="shrink-0">
        <circle cx={centre} cy={centre} r={(outerRadius + innerRadius) / 2} fill="none" stroke={pieChartAccentFill(color)} strokeWidth={outerRadius - innerRadius} />
        <circle cx={centre} cy={centre} r={outerRadius} fill="none" stroke={color} strokeWidth={SLICE_BORDER_WIDTH_PX} />
        <circle cx={centre} cy={centre} r={innerRadius} fill="none" stroke={color} strokeWidth={SLICE_BORDER_WIDTH_PX} />
      </svg>;
  }
  let startDeg = 0;
  return <svg width={sizePx} height={sizePx} viewBox={`0 0 ${sizePx} ${sizePx}`} aria-hidden className="shrink-0">
      {slices.map(slice_1 => {
      const spanDeg = slice_1.percent / total * 360;
      const gapDeg = spanDeg > SLICE_GAP_DEG ? SLICE_GAP_DEG : 0;
      const path = annularSectorPath(centre, outerRadius, innerRadius, startDeg + gapDeg / 2, startDeg + spanDeg - gapDeg / 2);
      startDeg += spanDeg;
      const color_0 = MODEL_ID_COLORS[slice_1.modelId];
      return <path key={slice_1.modelId} d={path} fill={pieChartAccentFill(color_0)} stroke={color_0} strokeWidth={SLICE_BORDER_WIDTH_PX} strokeLinejoin="round" />;
    })}
    </svg>;
}
type ModelMixPieProps = {
  mix: ModelMixSegment[];
  /** Names the mix in the hover panel, e.g. `Current mix`. */
  label: string;
  /** Carries the spend per model behind the mix, which the hover panel reads per tier. */
  calculationRows?: SavingsCalculationRow[];
  /** Which side of those rows this pie stands for. */
  spendField?: 'actualSpend' | 'optimalSpend';
  sizePx?: number;
  className?: string;
};

/** One mix as a pie, with the tier it leans on most spelled out beside it. */
export function ModelMixPie(t0) {
  const $ = _c(32);
  const {
    mix,
    label,
    calculationRows,
    spendField: t1,
    sizePx: t2,
    className
  } = t0;
  const spendField = t1 === undefined ? "actualSpend" : t1;
  const sizePx = t2 === undefined ? DEFAULT_SIZE_PX : t2;
  const anchorRef = useRef(null);
  const [tooltipPos, setTooltipPos] = useState(null);
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => {
      const rect = anchorRef.current?.getBoundingClientRect();
      if (!rect) {
        return;
      }
      setTooltipPos({
        x: rect.left + rect.width / 2,
        y: rect.top
      });
    };
    $[0] = t3;
  } else {
    t3 = $[0];
  }
  const showTooltip = t3;
  let t4;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => setTooltipPos(null);
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  const hideTooltip = t4;
  let t5;
  if ($[2] !== calculationRows || $[3] !== mix || $[4] !== spendField) {
    t5 = modelId => {
      const share = `${formatScore(mixPercent(mix, modelId), {
        decimals: 0
      })}%`;
      const spend = calculationRows?.find(row => row.modelId === modelId)?.[spendField];
      return spend != null ? `${formatAnnualSpend(spend)} · ${share}` : share;
    };
    $[2] = calculationRows;
    $[3] = mix;
    $[4] = spendField;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  const tierValue = t5;
  let t10;
  let t11;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[6] !== className || $[7] !== mix || $[8] !== sizePx) {
    const dominant = dominantMixSegment(mix);
    t6 = anchorRef;
    if ($[15] !== className) {
      t7 = cn("flex min-w-0 items-center gap-2", className);
      $[15] = className;
      $[16] = t7;
    } else {
      t7 = $[16];
    }
    t8 = showTooltip;
    t9 = hideTooltip;
    if ($[17] !== mix || $[18] !== sizePx) {
      t10 = <MixRing mix={mix} sizePx={sizePx} />;
      $[17] = mix;
      $[18] = sizePx;
      $[19] = t10;
    } else {
      t10 = $[19];
    }
    t11 = dominant ? <span className="flex min-w-0 items-center gap-1.5 text-xs text-foreground"><span className="truncate">{MODEL_TIER_LABELS[MODEL_TIERS[dominant.modelId]]}</span><span className={cn("shrink-0 text-muted-foreground", AIFI_DATA_NUMBER_CLASS)}>{formatScore(dominant.percent, {
          decimals: 0
        })}%</span></span> : <span className="text-xs text-muted-foreground">-</span>;
    $[6] = className;
    $[7] = mix;
    $[8] = sizePx;
    $[9] = t10;
    $[10] = t11;
    $[11] = t6;
    $[12] = t7;
    $[13] = t8;
    $[14] = t9;
  } else {
    t10 = $[9];
    t11 = $[10];
    t6 = $[11];
    t7 = $[12];
    t8 = $[13];
    t9 = $[14];
  }
  let t12;
  if ($[20] !== label || $[21] !== tierValue || $[22] !== tooltipPos) {
    t12 = tooltipPos ? <FloatingTooltip open={true} x={tooltipPos.x} y={tooltipPos.y}><ChartTooltipPanel title={label} rows={MODEL_ORDER.map(modelId_0 => ({
        key: modelId_0,
        label: MODEL_TIER_LABELS[MODEL_TIERS[modelId_0]],
        value: tierValue(modelId_0)
      }))} /></FloatingTooltip> : null;
    $[20] = label;
    $[21] = tierValue;
    $[22] = tooltipPos;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  let t13;
  if ($[24] !== t10 || $[25] !== t11 || $[26] !== t12 || $[27] !== t6 || $[28] !== t7 || $[29] !== t8 || $[30] !== t9) {
    t13 = <div ref={t6} className={t7} onMouseEnter={t8} onMouseLeave={t9}>{t10}{t11}{t12}</div>;
    $[24] = t10;
    $[25] = t11;
    $[26] = t12;
    $[27] = t6;
    $[28] = t7;
    $[29] = t8;
    $[30] = t9;
    $[31] = t13;
  } else {
    t13 = $[31];
  }
  return t13;
}

/** Placeholder matching the pie + dominant tier label layout in task table rows. */
export function ModelMixPieSkeleton(t0) {
  const $ = _c(6);
  const {
    className
  } = t0;
  let t1;
  if ($[0] !== className) {
    t1 = cn("flex min-w-0 items-center gap-2", className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <Skeleton className="size-[30px] shrink-0 rounded-full" />;
    t3 = <Skeleton className="h-3 w-20" />;
    $[2] = t2;
    $[3] = t3;
  } else {
    t2 = $[2];
    t3 = $[3];
  }
  let t4;
  if ($[4] !== t1) {
    t4 = <div className={t1}>{t2}{t3}</div>;
    $[4] = t1;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  return t4;
}